import { useEffect, useState } from 'react'
import React from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { billApi, accountApi, documentApi } from '@/services/api'
import type { Bill, Account, Document } from '@/types'
import { Receipt, Plus, CheckCircle, DollarSign, Ban, Trash2, Paperclip, File, FileText as FileTextIcon, Image } from 'lucide-react'
import toast from 'react-hot-toast'
import { formatCurrency } from '@/utils/currency'
import { cn } from '@/lib/utils'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Separator } from '@/components/ui/separator'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'

function formatSize(bytes: number | null) {
  if (!bytes) return '—'
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function DocIcon({ mimeType }: { mimeType: string }) {
  if (mimeType.startsWith('image/')) return <Image className="w-3.5 h-3.5 text-blue-400 shrink-0" />
  if (mimeType === 'application/pdf') return <FileTextIcon className="w-3.5 h-3.5 text-red-400 shrink-0" />
  return <File className="w-3.5 h-3.5 text-gray-400 shrink-0" />
}

const STATUS_TABS = ['all', 'draft', 'approved', 'paid', 'overdue', 'voided'] as const
type StatusTab = typeof STATUS_TABS[number]

const STATUS_VARIANT: Record<string, 'default' | 'info' | 'success' | 'destructive' | 'warning'> = {
  draft: 'default',
  approved: 'info',
  paid: 'success',
  overdue: 'destructive',
  voided: 'default',
}

function RecordPaymentModal({
  bill, accounts, onClose, onDone,
}: {
  bill: Bill; accounts: Account[]; onClose: () => void; onDone: (updated: Bill) => void
}) {
  const today = new Date().toISOString().slice(0, 10)
  const [form, setForm] = useState({ payment_date: today, amount: bill.amount_due, payment_account_id: '' })
  const [saving, setSaving] = useState(false)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)
    try {
      const payload: { payment_date: string; amount: string; payment_account_id?: string } = {
        payment_date: form.payment_date, amount: form.amount,
      }
      if (form.payment_account_id) payload.payment_account_id = form.payment_account_id
      const res = await billApi.recordPayment(bill.id, payload)
      onDone(res.data.bill)
      toast.success('Payment recorded')
      onClose()
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed to record payment')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Record Payment — {bill.bill_number}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <p className="text-xs text-[rgba(0,10,30,0.75)]">
            Amount due: <strong className="text-[#000A1E]">{formatCurrency(bill.amount_due, bill.currency)}</strong>
          </p>
          <div className="space-y-1.5">
            <Label>Payment Date</Label>
            <Input type="date" value={form.payment_date}
              onChange={(e) => setForm({ ...form, payment_date: e.target.value })} required />
          </div>
          <div className="space-y-1.5">
            <Label>Amount</Label>
            <Input type="number" step="0.01" min="0.01" value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })} required />
          </div>
          <div className="space-y-1.5">
            <Label>Payment Account <span className="text-[rgba(0,10,30,0.75)] font-normal">(optional)</span></Label>
            <select value={form.payment_account_id}
              onChange={(e) => setForm({ ...form, payment_account_id: e.target.value })} className="input">
              <option value="">— No GL entry —</option>
              {accounts.filter((a) => a.account_type === 'asset' && !a.is_archived).map((a) => (
                <option key={a.id} value={a.id}>{a.code} — {a.name}</option>
              ))}
            </select>
          </div>
          <Separator />
          <div className="flex gap-3">
            <Button type="submit" disabled={saving} className="flex-1">
              {saving ? 'Recording...' : 'Record Payment'}
            </Button>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default function BillList() {
  const { clientId } = useParams<{ clientId: string }>()
  const navigate = useNavigate()
  const [bills, setBills] = useState<Bill[]>([])
  const [accounts, setAccounts] = useState<Account[]>([])
  const [docs, setDocs] = useState<Document[]>([])
  const [activeTab, setActiveTab] = useState<StatusTab>('all')
  const [loading, setLoading] = useState(true)
  const [premiumGated, setPremiumGated] = useState(false)
  const [paymentBill, setPaymentBill] = useState<Bill | null>(null)
  const [expandedId, setExpandedId] = useState<string | null>(null)

  useEffect(() => {
    if (!clientId) return
    Promise.all([billApi.list(clientId), accountApi.list(clientId), documentApi.list(clientId)])
      .then(([billRes, accRes, docRes]) => {
        setBills(billRes.data.bills)
        setAccounts(accRes.data.accounts)
        setDocs(docRes.data.documents)
      })
      .catch((err: unknown) => {
        const status = (err as { response?: { status?: number } })?.response?.status
        if (status === 402) setPremiumGated(true)
        else toast.error('Failed to load bills')
      })
      .finally(() => setLoading(false))
  }, [clientId])

  function docsFor(billId: string) {
    return docs.filter((d) => d.linked_bill_id === billId)
  }

  const filtered = activeTab === 'all' ? bills : bills.filter((b) => b.status === activeTab)

  async function handleApprove(bill: Bill) {
    try {
      const res = await billApi.approve(bill.id)
      setBills((prev) => prev.map((b) => (b.id === res.data.bill.id ? res.data.bill : b)))
      toast.success('Bill approved')
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed')
    }
  }

  async function handleVoid(bill: Bill) {
    if (!confirm(`Void bill ${bill.bill_number}?`)) return
    try {
      const res = await billApi.void(bill.id)
      setBills((prev) => prev.map((b) => (b.id === res.data.bill.id ? res.data.bill : b)))
      toast.success('Bill voided')
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed')
    }
  }

  async function handleDelete(bill: Bill) {
    if (!confirm(`Delete draft bill ${bill.bill_number}?`)) return
    try {
      await billApi.delete(bill.id)
      setBills((prev) => prev.filter((b) => b.id !== bill.id))
      toast.success('Bill deleted')
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed')
    }
  }

  if (premiumGated) {
    return (
      <Card className="max-w-sm mx-auto mt-16">
        <CardContent className="p-10 text-center">
          <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center mx-auto mb-4">
            <Receipt className="w-6 h-6 text-amber-500" />
          </div>
          <h2 className="text-base font-semibold text-[#000A1E] mb-2">Premium Feature</h2>
          <p className="text-sm text-[rgba(0,10,30,0.75)] mb-5">
            Bills require a Premium client. Upgrade in Client Settings → Plan.
          </p>
          <Button onClick={() => navigate(`/clients/${clientId}/settings`)}>Upgrade to Premium</Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <h1 className="text-[17px] font-bold text-[#000A1E] leading-[24px]">Bills</h1>
          <p className="text-sm text-[rgba(0,10,30,0.75)] mt-0.5">Supplier bills and accounts payable</p>
        </div>
        <Button variant="create" onClick={() => navigate(`/clients/${clientId}/bills/new`)}>
          <Plus className="w-4 h-4" /> New Bill
        </Button>
      </div>

      {/* Status tabs */}
      <div className="flex gap-0 border-b-2 border-[#CCCED2]">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={cn(
              'px-4 py-3 text-[13px] -mb-[2px] border-b-[3px] capitalize transition-colors',
              activeTab === tab
                ? 'text-[#000A1E] font-bold border-b-[#0078C8]'
                : 'text-[#0078C8] font-normal border-b-transparent hover:text-[#000A1E]'
            )}
          >
            {tab === 'all' ? 'All' : tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-5 space-y-3">
              {[1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Bill #</TableHead>
                  <TableHead>Supplier</TableHead>
                  <TableHead>Issue Date</TableHead>
                  <TableHead>Due Date</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-right">Amount Due</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="py-10 text-center text-sm text-[rgba(0,10,30,0.75)]">
                      No bills found
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((bill) => (
                    <React.Fragment key={bill.id}>
                      <TableRow>
                        <TableCell>
                          <button
                            onClick={() => navigate(`/clients/${clientId}/bills/${bill.id}/edit`)}
                            className="text-sm text-[#0078C8] font-bold hover:underline"
                          >
                            {bill.bill_number}
                          </button>
                          {bill.purchase_order_number && (
                            <p className="text-[10px] text-[rgba(0,10,30,0.75)] mt-0.5">
                              From {bill.purchase_order_number}
                            </p>
                          )}
                        </TableCell>
                        <TableCell>
                          <p className="text-sm text-[#000A1E]">{bill.supplier_name}</p>
                          {bill.supplier_email && <p className="text-xs text-[rgba(0,10,30,0.75)]">{bill.supplier_email}</p>}
                        </TableCell>
                        <TableCell className="text-sm text-[rgba(0,10,30,0.75)]">{bill.issue_date}</TableCell>
                        <TableCell className="text-sm text-[rgba(0,10,30,0.75)]">{bill.due_date}</TableCell>
                        <TableCell className="text-right text-sm text-[rgba(0,10,30,0.75)] tabular-nums">
                          {formatCurrency(bill.total, bill.currency)}
                        </TableCell>
                        <TableCell className="text-right text-sm font-medium text-[#000A1E] tabular-nums">
                          {formatCurrency(bill.amount_due, bill.currency)}
                        </TableCell>
                        <TableCell>
                          <Badge variant={STATUS_VARIANT[bill.status] ?? 'default'}>
                            {bill.status.charAt(0).toUpperCase() + bill.status.slice(1)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              onClick={() => setExpandedId(expandedId === bill.id ? null : bill.id)}
                              title="Attachments"
                            >
                              <Paperclip className={cn(
                                'w-3.5 h-3.5',
                                docsFor(bill.id).length > 0 ? 'text-blue-500' : 'text-gray-300',
                              )} />
                            </Button>
                            {bill.status === 'draft' && (
                              <Button variant="ghost" size="icon-sm" onClick={() => handleApprove(bill)} title="Approve bill">
                                <CheckCircle className="w-3.5 h-3.5 text-blue-500" />
                              </Button>
                            )}
                            {(bill.status === 'approved' || bill.status === 'overdue') && (
                              <Button variant="ghost" size="icon-sm" onClick={() => setPaymentBill(bill)} title="Record payment">
                                <DollarSign className="w-3.5 h-3.5 text-emerald-600" />
                              </Button>
                            )}
                            {(bill.status === 'draft' || bill.status === 'approved') && (
                              <Button variant="ghost" size="icon-sm" onClick={() => handleVoid(bill)} title="Void bill">
                                <Ban className="w-3.5 h-3.5 text-amber-500" />
                              </Button>
                            )}
                            {bill.status === 'draft' && (
                              <Button variant="ghost" size="icon-sm" onClick={() => handleDelete(bill)} title="Delete"
                                className="text-red-400 hover:text-red-600 hover:bg-red-50">
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                      {expandedId === bill.id && (
                        <TableRow className="bg-muted/30 hover:bg-muted/30">
                          <TableCell colSpan={8} className="px-6 py-3">
                            {docsFor(bill.id).length === 0 ? (
                              <p className="text-xs text-[rgba(0,10,30,0.75)]">No documents attached to this bill.</p>
                            ) : (
                              <div className="flex flex-wrap gap-3">
                                {docsFor(bill.id).map((doc) => (
                                  <a
                                    key={doc.id}
                                    href={doc.file_path}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="flex items-center gap-1.5 text-xs text-[rgba(0,10,30,0.75)] hover:text-[#0078C8] bg-white border border-border rounded-md px-2.5 py-1.5 transition-colors"
                                  >
                                    <DocIcon mimeType={doc.mime_type} />
                                    <span className="font-medium truncate max-w-[160px]">{doc.file_name}</span>
                                    {doc.category && (
                                      <Badge variant="secondary" className="capitalize text-[10px] px-1 py-0">
                                        {doc.category}
                                      </Badge>
                                    )}
                                    <span className="text-[rgba(0,10,30,0.75)]">{formatSize(doc.file_size)}</span>
                                  </a>
                                ))}
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {paymentBill && (
        <RecordPaymentModal
          bill={paymentBill}
          accounts={accounts}
          onClose={() => setPaymentBill(null)}
          onDone={(updated) => {
            setBills((prev) => prev.map((b) => (b.id === updated.id ? updated : b)))
            setPaymentBill(null)
          }}
        />
      )}
    </div>
  )
}
